import Vue from "vue";
import { BankCustodian } from "@/admin/types";
import {
    AccountVerificationStatus,
    AchVerifier,
    BankAccountDefaultsRequest,
    BankAccountState,
    BankAccountVerificationAccountHolderResponse,
    BankAchAccountChoiceModel,
    BeneficiaryShare,
    BeneficiaryUpdateRequest,
    Card,
    FetchState,
    LinkedAccountApi,
    LinkedAccountsGetResponse,
    LinkedAccountState,
    LinkedBankAccountUse,
    LinkedBankAccountUseStatus,
    LinkedBankAccountVerificationStatus,
    LinkedCardState,
    LinkedVerificationState,
    MicroEntryVerificationAccount,
    UserPermission,
    VerificationSubmission
} from "@/shared/types";
import * as bankService from "@/shared/api/bankService";
import * as expenseService from "@/shared/api/expenseService";
import * as financialService from "@/shared/api/financialService";
import * as storeHelper from "@/shared/storeHelper";
import * as _ from "lodash";
import * as umb from "@/shared/umbUrls";
import { AxiosResponse } from "axios";
import { defineStore } from "pinia";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useNotifierStore } from "@/shared/store/notifier";
import { errorIsStatus } from "@/shared/errorHelper";
import { usePersonalContributionSchedulesStore } from "@/shared/store/personalContributionSchedules";
import { useUserStore } from "@/shared/store/user";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";

export const initialState: BankAccountState = {
    fetchState: FetchState.Uninitialized,
    linkedAccountsFetchState: FetchState.Uninitialized,
    accountNumber: null,
    linkedCards: [],
    linkedAccounts: [],
    microEntryVerificationAccounts: [],
    microEntryVerificationAccountsFetchState: FetchState.Uninitialized,
    linkedAccountState: LinkedAccountState.Loading,
    linkedCardState: LinkedCardState.Loading,
    linkedCardNeedsMaintenance: null,
    linkedCardNeedsMaintenanceFetchState: FetchState.Uninitialized,
    verificationResponse: null,
    linkedVerificationState: LinkedVerificationState.Uninitialized,
    linkFinancialCardUrl: null,
    linkedCardMaintenanceUrl: null,
    isLinkedCardIframeLoading: false,
    achVerifier: AchVerifier.None,
    achVerifierUrl: null,
    achVerifierUrlState: FetchState.Uninitialized,
    isAchVerifyIFrameLoaded: false,
    isAccountDelayInfoPanelLoaded: false,
    bankAchAccountChoices: [],
    defaultContributionAccountId: null,
    defaultReimbursementAccountId: null,
    defaultScheduledContributionAccountId: null,
    hasDirectDepositAccount: false,
    hasMissingUses: false,
    isPendingAccount: false,
    consumerBankAccountGetDateCreated: null,
    consumerBankAccountGetHttpStatusCode: "ok",
    consumerBankAccountGetErrorMessage: null,
    isAccountClosed: false,
    accountStatusFetchState: FetchState.Uninitialized,
    priorYearContributionsCutoffDate: null,
    beneficiaries: {},
    beneficiariesFetchState: FetchState.Uninitialized,
};

function linkedAccountOrDefault<TType> (state: BankAccountState, defaultValue: TType,
                                        action: (account: LinkedAccountApi) => TType): TType {
    if (state.linkedAccounts && state.linkedAccounts.length > 0) {
        return action(state.linkedAccounts[0]);
    }

    return defaultValue;
}

function setLinkedAccountValue (state: BankAccountState, action: (account: LinkedAccountApi) => void): void {
    if (!state.linkedAccounts || state.linkedAccounts.length === 0) {
        state.linkedAccounts = [ {
            use: null,
            bankAccountNumber: "",
            bankRoutingNumber: "",
            bankName: "",
            linkedBankAccountId: 0,
            verificationStatus: AccountVerificationStatus.Pending,
            bankAccountName: "",
            linkedBankAccountType: "checking",
            hasPersonalContributionSchedules: false,
            isTokenizedAccountNumber: false,
            apiErrorTimestamp: null,
            apiErrorStatusCode: null,
            apiErrorMessage: null
        } ];
    }

    action(state.linkedAccounts[0]);
}

export const useBankAccountStore = defineStore("bankAccount", {
    state: (): BankAccountState => (_.cloneDeep(initialState)),
    getters: {
        linkedAccountsLoaded: state => state.linkedAccountsFetchState === FetchState.Loaded,
        linkedAccountExists: state =>
            state.linkedAccounts && state.linkedAccounts.length > 0 && state.linkedAccounts[0].linkedBankAccountId > 0,
        verifiedLinkedAccounts: state => {
            if (state.linkedAccountsFetchState !== FetchState.Loaded) return [];
            return state.linkedAccounts.filter(v => v.verificationStatus === "verified");
        },
        sortedLinkedContributionAccounts () {
            return (k: "defaultContributionAccountId" | "defaultScheduledContributionAccountId") => {

                const linkedAccounts: LinkedAccountApi[] = this.verifiedLinkedAccounts;
                const defaultId = _.get(this, k, -1);
                if (defaultId === -1) {
                    return linkedAccounts;
                }
                return [ ...linkedAccounts ].sort((a, b) => {
                    const valA = (a.linkedBankAccountId === defaultId ? 0 : 1);
                    const valB = (b.linkedBankAccountId === defaultId ? 0 : 1);
                    return valA < valB ? -1 : (valB < valA) ? 1 : 0;
                });
            };
        },
        defaultContributionAccount: state =>
            state.linkedAccounts.find(a => a.linkedBankAccountId === state.defaultContributionAccountId),

        defaultReimbursementAccount: state =>
            state.linkedAccounts.find(a => a.linkedBankAccountId === state.defaultReimbursementAccountId),
        activeDirectDepositAccount: state => state.linkedAccounts.find(a => a.use !== undefined && a.use !== null &&
            a.use.accountUse === LinkedBankAccountUse.DirectDeposit && a.use.status === LinkedBankAccountUseStatus.Active),

        defaultScheduledContributionAccount: state =>
            state.linkedAccounts.find(a => a.linkedBankAccountId === state.defaultScheduledContributionAccountId),
        isMissingUses: state => state.hasMissingUses,
        pendingAccount: state => state.isPendingAccount,
        hasDirectDeposit: state => state.hasDirectDepositAccount,
        firstLinkedAccount: state => state.linkedAccounts.length ? _.head(state.linkedAccounts) : null,
        linkedAccount: state => linkedAccountOrDefault(state, null, _.identity),
        linkedAccountType: state => linkedAccountOrDefault(state, "Checking",
            account => _.capitalize(account.linkedBankAccountType)),
        hasAtLeastOneLinkedAccount: state => state.linkedAccounts && state.linkedAccounts.length > 0,
        linkedAccountNumber: state => linkedAccountOrDefault(state, "", account => account.bankAccountNumber),
        linkedAccountRouting: state => linkedAccountOrDefault(state, "", account => account.bankRoutingNumber),
        linkedAccountName: state => linkedAccountOrDefault(state, "", account => account.bankAccountName),
        linkedAccountBankName: state => linkedAccountOrDefault(state, "", account => account.bankName),
        linkedAccountIsNew: state => linkedAccountOrDefault(state, 0, account => account.linkedBankAccountId) === 0,
        linkedAccountVerificationStatus: state => linkedAccountOrDefault(state,
            "None", account => _.capitalize(account.verificationStatus)),
        linkedMaskedAccountNumber: state => linkedAccountOrDefault(state, "", account => account.bankAccountNumber),
        linkedAccountRequiresVerification: state => linkedAccountOrDefault(state,
            false, account => account.verificationStatus === AccountVerificationStatus.Pending || account.verificationStatus === AccountVerificationStatus.Failed ||
                account.verificationStatus === AccountVerificationStatus.Expired),
        linkedAccountResubmitVerification: state => linkedAccountOrDefault(state,
            false, account => account.verificationStatus === AccountVerificationStatus.Failed ||
                account.verificationStatus === AccountVerificationStatus.Expired),
        linkedAccountIsLink: state => state.linkedAccountState === LinkedAccountState.Link,
        linkedAccountIsList: state => state.linkedAccountState === LinkedAccountState.List,
        linkedAccountIsForm: state => state.linkedAccountState === LinkedAccountState.Form,
        linkedAccountIsLoading: state => state.linkedAccountState === LinkedAccountState.Loading,
        linkedAccountIsSuccess: state => state.linkedAccountState === LinkedAccountState.Success,
        linkedAccountIsChoice: state => state.linkedAccountState === LinkedAccountState.Choice,
        linkedAccountIsVerify: state => state.linkedAccountState === LinkedAccountState.Verify,
        linkedCardViewIsForm: state => state.linkedCardState === LinkedCardState.Form,
        linkedCardViewIsList: state => state.linkedCardState === LinkedCardState.List,
        linkedCardViewIsLink: state => state.linkedCardState === LinkedCardState.Link,
        linkedCardViewIsLoading: state => state.linkedCardState === LinkedCardState.Loading,
        isAchIFrameLoaded: state => state.isAchVerifyIFrameLoaded,
        isAchVerifierUrlLoading: state => state.achVerifierUrlState === FetchState.Loading,
        isBankAccountClosed: state => state.isAccountClosed,
        isBeneficiariesLoaded: state => state.beneficiariesFetchState === FetchState.Loaded,
        canAdministerLinkedAccounts: state => {
            const rolePermissionsStore = useRolePermissionsStore();
            return rolePermissionsStore.userHasAdminPermission(UserPermission.AdminPortalManageExternalLinkedBankAccounts);
        },
        canViewLinkedAccountNumbers: state => {
            const rolePermissionsStore = useRolePermissionsStore();
            return rolePermissionsStore.userHasAdminPermission(UserPermission.AdminPortalViewRealBankAccountNumbers);
        },
        isConsumerLinkedAccountsInErrorState: state => state.consumerBankAccountGetHttpStatusCode !== "ok",
        isAnyAccountsInVerificationFailedState: state => _.some(state.linkedAccounts, x => x.verificationStatus === AccountVerificationStatus.Failed),
        consumerLinkedAccountsGetStatusCode: state => state.consumerBankAccountGetHttpStatusCode,
        consumerLinkedAccountsGetErrorMessage: state => state.consumerBankAccountGetErrorMessage,
        consumerLinkedAccountsGetDateCreated: state => state.consumerBankAccountGetDateCreated
    },
    actions: {
        setBankAccountStateLoading (): void {
            this.fetchState = FetchState.Loading;
        },
        setLinkedBankAccountsFetchState (newFetchState: FetchState) {
            this.linkedAccountsFetchState = newFetchState;
        },
        setAccountNumber (accountNumber: string): void {
            this.accountNumber = accountNumber;
        },
        setLinkedAccountState (linkedAccountState: LinkedAccountState): void {
            this.linkedAccountState = linkedAccountState;
        },
        setLinkedCardState (linkedCardState: LinkedCardState): void {
            this.linkedCardState = linkedCardState;
        },
        setLinkedCardIframeLoading (linkedCardIframeLoading: boolean): void {
            this.isLinkedCardIframeLoading = linkedCardIframeLoading;
        },
        setVerificationResponse (
            verificationResponse: LinkedBankAccountVerificationStatus): void {
            this.verificationResponse = verificationResponse;
        },
        setAccountName (payload: { linkedBankAccountId: number, name: string }): void {
            const account = this.linkedAccounts.find(a => a.linkedBankAccountId === payload.linkedBankAccountId);
            if (!account) {
                return;
            }

            account.bankAccountName = payload.name;
        },
        setLinkedAccount (apiAccount: LinkedAccountApi): void {
            if (!this.linkedAccounts) {
                this.linkedAccounts = [];
            }

            const i = this.linkedAccounts.findIndex(a => a.linkedBankAccountId === apiAccount.linkedBankAccountId);
            if (i < 0) {
                this.linkedAccounts = [ ...this.linkedAccounts, apiAccount ];
            } else {
                Vue.set(this.linkedAccounts, i, apiAccount);
            }
        },
        removeLinkedAccount (linkedBankAccountId: number | undefined): void {
            if (!this.linkedAccounts) {
                this.linkedAccounts = [];
            }

            const i = (linkedBankAccountId === undefined)
                ? 0
                : this.linkedAccounts.findIndex(x => x.linkedBankAccountId === linkedBankAccountId);

            this.linkedAccounts = [
                ...this.linkedAccounts.slice(0, i),
                ...this.linkedAccounts.slice(i + 1)
            ];

            if (this.linkedAccounts.length === 0) {
                this.linkedAccountState = LinkedAccountState.Link;
                return;
            }

            this.linkedAccountState = LinkedAccountState.List;
        },
        removeMicroEntryVerificationAccount (microEntryVerificationAccountId: number): void {
            if (!this.microEntryVerificationAccounts) {
                this.microEntryVerificationAccounts = [];
                return;
            }
            if (microEntryVerificationAccountId === undefined || microEntryVerificationAccountId === 0) {
                return;
            }

            this.microEntryVerificationAccounts = this.microEntryVerificationAccounts.filter(x =>
                x.microDepositAccountVerificationId !== microEntryVerificationAccountId);
        },
        addCard (card: Card): void {
            this.linkedCards = [ ...this.linkedCards, card ];
        },
        setFetchState (fetchState: FetchState): void {
            this.fetchState = fetchState;
        },
        setLinkedAccounts (linkedAccounts: LinkedAccountApi[]): void {
            this.linkedAccounts = linkedAccounts;
            this.linkedAccountsFetchState = FetchState.Loaded;
            if (!linkedAccounts || linkedAccounts.length === 0) {
                this.linkedAccountState = LinkedAccountState.Link;
            } else {
                this.linkedAccountState = LinkedAccountState.List;
            }
        },
        setLinkedAccountGetStatus (getResponse: LinkedAccountsGetResponse): void {
          this.consumerBankAccountGetDateCreated = getResponse.consumerBankAccountGetDateCreated;
          this.consumerBankAccountGetHttpStatusCode = getResponse.consumerBankAccountGetHttpStatusCode;
          this.consumerBankAccountGetErrorMessage = getResponse.consumerBankAccountGetErrorMessage;
        },
        setMicroEntryVerificationAccounts (
            microEntryVerificationAccount: MicroEntryVerificationAccount[]) {
            this.microEntryVerificationAccounts = microEntryVerificationAccount;
            this.microEntryVerificationAccountsFetchState = FetchState.Loaded;
        },
        setDefaultLinkedAccountIds (
            defaults: BankAccountDefaultsRequest): void {
            const positiveOrNull = (n: number) => n > 0 ? n : null;
            this.hasDirectDepositAccount = defaults.hasDirectDepositAccount;
            this.hasMissingUses = defaults.hasMissingUses;
            this.isPendingAccount = defaults.isPendingAccount;
            this.defaultContributionAccountId = positiveOrNull(defaults.defaultContributionAccountId);
            this.defaultReimbursementAccountId = positiveOrNull(defaults.defaultReimbursementAccountId);
            this.defaultScheduledContributionAccountId = positiveOrNull(
                defaults.defaultScheduledContributionAccountId);
        },
        setLinkedCards (linkedCards: Card[]): void {
            if (linkedCards.length > 0) {
                this.linkedCardState = LinkedCardState.List;
            }
            this.linkedCards = linkedCards;
        },
        setLinkFinancialCardUrl (linkFinancialCardUrl: string): void {
            this.linkFinancialCardUrl = linkFinancialCardUrl;
        },
        setLinkedCardNeedsMaintenanceFetchState (fetchState: FetchState): void {
            this.linkedCardNeedsMaintenanceFetchState = fetchState;
        },
        setLinkedCardMaintenanceUrl (linkedCardMaintenanceUrl: string): void {
            this.linkedCardMaintenanceUrl = linkedCardMaintenanceUrl;
        },
        setLinkedCardNeedsMaintenance (needsMaintenance: boolean): void {
            this.linkedCardNeedsMaintenance = needsMaintenance;
        },
        setAchVerifierUrl (achVerifierUrl: string): void {
            this.achVerifierUrl = achVerifierUrl; // TODO: who calls this? and when?
        },
        setAchVerifierUrlState (fetchState: FetchState): void {
            this.achVerifierUrlState = fetchState;
        },
        setAchVerifyIFrameLoaded (isLoaded: boolean): void {
            this.isAchVerifyIFrameLoaded = isLoaded;
        },
        setAccountDelayInfoPanelLoaded (isLoaded: boolean): void {
            this.isAccountDelayInfoPanelLoaded = isLoaded;
        },
        setAchVerifier (achVerifier: AchVerifier) {
            this.achVerifier = achVerifier;
        },
        clearLinkedBankAccounts () {
            this.linkedAccountState = LinkedAccountState.Loading;
        },
        setAchBankChoices (choices: BankAchAccountChoiceModel[]) {
            this.bankAchAccountChoices = choices;
        },
        resetAchBankChoices () {
            this.bankAchAccountChoices = [];
        },
        setBankAccountStatus (isAccountClosed: boolean) {
            this.isAccountClosed = isAccountClosed;
        },
        setBankAccountStatusFetchState (fetchState: FetchState) {
            this.accountStatusFetchState = fetchState;
        },
        setPriorYearContributionsCutoffDate (date: string) {
            this.priorYearContributionsCutoffDate = date;
        },
        loadLinkedBankAccountsForUser (options?: { username?: string, partnerId?: number } ) {
          options = options || {};
          this.setLinkedBankAccountsFetchState(FetchState.Loading);
          return fetchLinkedBankAccounts(options.username, options.partnerId)
            .then(response => {
              this.setLinkedAccounts(response.data.linkedBankAccounts);
              this.setLinkedAccountGetStatus(response.data);
              this.setAchVerifier(response.data.verificationMethod);
              this.setDefaultLinkedAccountIds(response.data);
            })
            .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        loadLinkedBankAccounts () {
            if (this.linkedAccountsFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setLinkedBankAccountsFetchState(FetchState.Loading);

            const methodApiCalls: ApiCallInput = {
                defaultApiCall: bankService.umbGetLinkedAccount,
                umbApiCall: bankService.umbGetLinkedAccount,
                externalAccountApiCall: fetchLinkedBankAccounts
            };

            const apiCall = determineApiCallToUse(methodApiCalls);

            return apiCall()
                .then(response => {
                    this.setLinkedAccounts(response.data.linkedBankAccounts);
                    this.setLinkedAccountGetStatus(response.data);
                    this.setMicroEntryVerificationAccounts(response.data.microEntryVerificationAccounts);
                    this.setDefaultLinkedAccountIds(response.data);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        loadBankAccountState (force?: boolean): Promise<any> {
            if (!force && this.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.retrieveLinkedCardsList();

            this.setFetchState(FetchState.Loading);

            const methodApiCalls: ApiCallInput = {
                defaultApiCall: fetchLinkedBankAccounts,
                umbApiCall: bankService.umbGetLinkedAccount,
                externalAccountApiCall: fetchLinkedBankAccounts
            };

            const apiCall = determineApiCallToUse(methodApiCalls);

            return apiCall().then(response => {
                this.setFetchState(FetchState.Loaded);
                const linkedBankAccounts = response.data.linkedBankAccounts;
                this.setLinkedAccounts(response.data.linkedBankAccounts);
                this.setLinkedAccountGetStatus(response.data);
                this.setMicroEntryVerificationAccounts(response.data.microEntryVerificationAccounts);
                this.setAchVerifier(response.data.verificationMethod);
                this.setDefaultLinkedAccountIds(response.data);

                if (!linkedBankAccounts || linkedBankAccounts.length === 0) {
                    this.setLinkedAccountState(LinkedAccountState.Link);
                } else {
                    this.setLinkedAccountState(LinkedAccountState.List);
                }
            }).catch(err => {
                this.setFetchState(FetchState.Loaded);
                this.setLinkedAccountState(LinkedAccountState.Form);
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        reSyncLinkedBankAccounts (options?: { username?: string, partnerId?: number } ): Promise<any> {
            options = options || {};
            this.setFetchState(FetchState.Loading);
            return bankService.externalAccountLinkedAccountReSync(options.username, options.partnerId)
              .then(response => {
                this.setFetchState(FetchState.Loaded);
                const linkedBankAccounts = response.data.linkedBankAccounts;
                this.setLinkedAccounts(linkedBankAccounts);
                this.setLinkedAccountGetStatus(response.data);
                this.setAchVerifier(response.data.verificationMethod);
                this.setDefaultLinkedAccountIds(response.data);

                if (!linkedBankAccounts || linkedBankAccounts.length === 0) {
                    this.setLinkedAccountState(LinkedAccountState.Link);
                } else {
                    this.setLinkedAccountState(LinkedAccountState.List);
                }
                const notifierStore = useNotifierStore();
                notifierStore.setSuccess("External Linked Bank Accounts have been synchronized", notifierStore.shortTimeout);
            }).catch(err => {
                this.setFetchState(FetchState.Loaded);
                this.setLinkedAccountState(LinkedAccountState.Form);
                storeHelper.handleError({ error: err, rethrow: true, timeout: 5000 });
            });
        },
        putLinkedAccount (
            linkedBankAccount: undefined | LinkedAccountApi): Promise<any> {
            if (this.linkedAccountState === LinkedAccountState.Loading) {
                return Promise.resolve();
            }

            if (!this.linkedAccounts) {
                return Promise.resolve();
            }

            // if the argument wasn't passed,
                // take the first element in the array (legacy behavior from when single account was allow)
            // if the argument isn't null, but has a falsy ID,
                 // then assume it's new and it won't be in the existing list
            // if the argument isn't null AND the ID isn't null, then get the version from state and put that one
            const linkedAccount = !linkedBankAccount
                ? this.linkedAccounts[0]
                : !linkedBankAccount.linkedBankAccountId
                    ? linkedBankAccount
                    : this.linkedAccounts.find(a => a.linkedBankAccountId === linkedBankAccount.linkedBankAccountId);

            if (!linkedAccount) {
                return Promise.resolve();
            }

            this.setLinkedAccountState(LinkedAccountState.Loading);

            let apiCall;
            if (linkedAccount.linkedBankAccountId) {
                const methodApiCalls: ApiCallInput = {
                    defaultApiCall: bankService.externalAccountUpdateLinkedAccount,
                    umbApiCall: bankService.umbUpdateLinkedAccount,
                    externalAccountApiCall: bankService.externalAccountUpdateLinkedAccount
                };

                apiCall = determineApiCallToUse(methodApiCalls);

            } else {
                const methodApiCalls: ApiCallInput = {
                    defaultApiCall: bankService.umbAddLinkedAccount,
                    umbApiCall: bankService.umbAddLinkedAccount,
                    externalAccountApiCall: bankService.umbAddLinkedAccount
                };

                apiCall = determineApiCallToUse(methodApiCalls);
            }

            return apiCall(linkedAccount).then(response => {
                if (linkedAccount.linkedBankAccountId) {
                    this.setLinkedAccountState(LinkedAccountState.List);
                } else {
                    this.setLinkedAccountState(LinkedAccountState.Success);
                }

                this.setLinkedAccount(response.data);
                this.setLinkedBankAccountsFetchState(FetchState.Uninitialized);
                return this.loadLinkedBankAccounts();
            }).catch(err => {
                this.setLinkedAccountState(LinkedAccountState.Form);
                this.loadBankAccountState(true);
                const custodianSettingStore = useCustodianSettingsStore();
                let error;
                if (custodianSettingStore.isHsaBankCustodian) {
                 error = "Sorry! We are unable to connect your account right now.";
                } else {
                    // tslint:disable-next-line:max-line-length
                    error = `Sorry! We are unable to connect your account right now. You can manually request the <a target="_blank" href="${umb.hsaCombinedContributionFormHref}">contributions</a> or <a target="_blank" href="${umb.hsaMsaWithdrawalFormHref}">reimbursements</a> by completing and submitting these forms.`;
                }
                storeHelper.handleError({
                    error,
                    rethrow: false,
                    timeout: 5000
                });
            });
        },
        updateDefaultAccounts (
            payload: BankAccountDefaultsRequest): Promise<any> {

            this.setLinkedAccountState(LinkedAccountState.Loading);

            const methodApiCalls: ApiCallInput = {
                defaultApiCall: bankService.externalAccountUpdateDefault,
                umbApiCall: bankService.umbLinkedAccountsUpdateDefault,
                externalAccountApiCall: bankService.externalAccountUpdateDefault
            };

            const apiCall = determineApiCallToUse(methodApiCalls);

            return apiCall(payload)
                .then(response => {
                    this.setDefaultLinkedAccountIds(response.data);
                    this.setLinkedAccountState(LinkedAccountState.List);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        adminDeleteLinkedAccount (options: { linkedAccountToDelete: LinkedAccountApi, username: string, partnerId: number }) {
          if (!options.linkedAccountToDelete) {
            return Promise.resolve();
          }
          const linkedAccount = options.linkedAccountToDelete;
          this.setLinkedAccountState(LinkedAccountState.Loading);
          return bankService.externalAccountAdminDeleteLinkedAccount(linkedAccount, options.username, options.partnerId)
            .then(response => {
              if (response.data) {
                this.removeLinkedAccount(linkedAccount.linkedBankAccountId);
              } else {
                storeHelper.handleError({ error: "Failed to delete linked account. Contact support to determine the reason.", rethrow: true, timeout: 5000 });
                this.setLinkedAccountState(LinkedAccountState.List);
                return Promise.resolve();
              }
              this.setLinkedBankAccountsFetchState(FetchState.Uninitialized);
              return this.loadLinkedBankAccountsForUser({ username: options.username, partnerId: options.partnerId });
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true, timeout: 5000 });
            });
        },
        adminChangeLinkedAccountType (options: { linkedAccountToReplace: LinkedAccountApi, username: string, partnerId: number }) {
            if (!options.linkedAccountToReplace) {
                return Promise.resolve();
            }
            const linkedAccount = options.linkedAccountToReplace;
            this.setLinkedAccountState(LinkedAccountState.Loading);
            return bankService.externalAccountAdminChangeLinkedAccountType(linkedAccount, options.username, options.partnerId)
                .then(response => {
                    if (!response.data) {
                        storeHelper.handleError({ error: "Failed to change the linked bank account type. Contact support to determine the reason.", rethrow: true, timeout: 5000 });
                        this.setLinkedAccountState(LinkedAccountState.List);
                        return Promise.resolve();
                    }
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("The linked bank account type has been changed", notifierStore.shortTimeout);
                    this.setLinkedBankAccountsFetchState(FetchState.Uninitialized);
                }).catch(err => {
                    storeHelper.handleError({ error: "Failed to change the linked bank account type. Contact support to determine the reason.", rethrow: true, timeout: 5000 });
                }) .finally(() => {
                    return this.loadLinkedBankAccountsForUser({ username: options.username, partnerId: options.partnerId });
                });
        },
        adminReplaceLinkedAccount (options: { linkedAccountToReplace: LinkedAccountApi, username: string, partnerId: number }) {
          if (!options.linkedAccountToReplace) {
            return Promise.resolve();
          }
          const linkedAccount = options.linkedAccountToReplace;
          this.setLinkedAccountState(LinkedAccountState.Loading);
          return bankService.externalAccountAdminReplaceLinkedAccount(linkedAccount, options.username, options.partnerId)
            .then(response => {
              if (response.data) {
                this.removeLinkedAccount(linkedAccount.linkedBankAccountId);
              } else {
                storeHelper.handleError({ error: "Failed to replace linked account. Contact support to determine the reason.", rethrow: true, timeout: 5000 });
                this.setLinkedAccountState(LinkedAccountState.List);
                return Promise.resolve();
              }
              this.setLinkedBankAccountsFetchState(FetchState.Uninitialized);
              return this.loadLinkedBankAccountsForUser({ username: options.username, partnerId: options.partnerId });
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true, timeout: 5000 });
            });
        },
        adminResyncLinkedAccounts (options: { username: string }): Promise<any> {
            this.setFetchState(FetchState.Loading);
            return bankService.externalAccountLinkedBankAccountAdminResync(options.username)
                .then(response => {
                    this.setFetchState(FetchState.Loaded);
                    const linkedBankAccounts = response.data.linkedBankAccounts;
                    this.setLinkedAccounts(linkedBankAccounts);
                    this.setLinkedAccountGetStatus(response.data);
                    this.setAchVerifier(response.data.verificationMethod);
                    this.setDefaultLinkedAccountIds(response.data);

                    if (!linkedBankAccounts || linkedBankAccounts.length === 0) {
                        this.setLinkedAccountState(LinkedAccountState.Link);
                    } else {
                        this.setLinkedAccountState(LinkedAccountState.List);
                    }
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("External Linked Bank Accounts have been synchronized", notifierStore.shortTimeout);
                }).catch(err => {
                    this.setFetchState(FetchState.Loaded);
                    this.setLinkedAccountState(LinkedAccountState.Form);
                    storeHelper.handleError({ error: err, rethrow: true, timeout: 5000 });
                });
        },
        deleteLinkedAccount (
            linkedAccountToDelete: undefined | LinkedAccountApi
        ): Promise<any> {
            if (!this.linkedAccounts || this.linkedAccounts.length === 0) {
                return Promise.resolve();
            }

            const linkedAccount = linkedAccountToDelete || this.linkedAccounts[0];
            if (!linkedAccount.linkedBankAccountId) {
                return Promise.resolve();
            }

            this.setLinkedAccountState(LinkedAccountState.Loading);

            const methodApiCalls: ApiCallInput = {
                defaultApiCall: bankService.externalAccountDeleteLinkedAccount,
                umbApiCall: bankService.umbDeleteLinkedAccount,
                externalAccountApiCall: bankService.externalAccountDeleteLinkedAccount
            };

            const apiCall = determineApiCallToUse(methodApiCalls);

            return apiCall(linkedAccount).then(response => {
                if (response.data) {
                    this.removeLinkedAccount(linkedAccount.linkedBankAccountId);
                    const contributionSchedulesStore = usePersonalContributionSchedulesStore();
                    contributionSchedulesStore.resetSchedulesFetchState();
                } else {
                    storeHelper.handleError({ error: "Failed to delete linked account", rethrow: true });
                    this.setLinkedAccountState(LinkedAccountState.List);
                    return Promise.resolve();
                }

                this.setLinkedBankAccountsFetchState(FetchState.Uninitialized);
                return this.loadLinkedBankAccounts();
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        verifyLinkedAccount (
            verificationSubmission: VerificationSubmission
        ): Promise<any> {
            if (!this.linkedAccounts || this.linkedAccounts.length === 0) {
                return Promise.resolve();
            }

            const linkedAccount = verificationSubmission.linkedBankAccountId
                ? this.linkedAccounts.find(a => a.linkedBankAccountId === verificationSubmission.linkedBankAccountId)
                : this.linkedAccounts[0];

            if (!linkedAccount || !linkedAccount.linkedBankAccountId) {
                return Promise.resolve();
            }
            this.setLinkedAccountState(LinkedAccountState.Loading);

            const methodApiCalls: ApiCallInput = {
                defaultApiCall: bankService.umbVerifyLinkedAccount,
                umbApiCall: bankService.umbVerifyLinkedAccount,
                externalAccountApiCall: bankService.umbVerifyLinkedAccount
            };

            const apiCall = determineApiCallToUse(methodApiCalls);

            return apiCall(linkedAccount.linkedBankAccountId, verificationSubmission)
                .then(response => {
                    this.setLinkedAccount(response.data);
                    this.setLinkedAccountState(LinkedAccountState.List);
                    this.setVerificationResponse(LinkedBankAccountVerificationStatus.Verified);

                    if ([ "pending", "failed", "expired" ].indexOf(response.data.verificationStatus) > -1) {
                        storeHelper.handleError({
                            error: "The verification amounts do not match. Please double check the values.",
                            rethrow: false
                        });
                    }
                }).catch(err => {
                    this.setLinkedAccountState(LinkedAccountState.List);
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        reVerifyLinkedAccount (): Promise<any> {
            if (!this.linkedAccounts || this.linkedAccounts.length === 0) {
                return Promise.resolve();
            }

            const linkedAccount = this.linkedAccounts[0];
            if (!linkedAccount.linkedBankAccountId) {
                return Promise.resolve();
            }
            this.setLinkedAccountState(LinkedAccountState.Loading);

            const methodApiCalls: ApiCallInput = {
                defaultApiCall: bankService.umbReVerifyLinkedAccount,
                umbApiCall: bankService.umbReVerifyLinkedAccount,
                externalAccountApiCall: bankService.umbReVerifyLinkedAccount
            };

            const apiCall = determineApiCallToUse(methodApiCalls);

            return apiCall(linkedAccount.linkedBankAccountId)
                .then(response => {
                    this.setLinkedAccount(response.data);
                    this.setLinkedAccountState(LinkedAccountState.List);
                    this.setVerificationResponse(LinkedBankAccountVerificationStatus.Pending);
                }).catch(err => {
                    this.setLinkedAccountState(LinkedAccountState.List);
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        retrieveLinkFinancialCardUrl (): Promise<any> {
            if (!this.linkFinancialCardUrl) {
                this.setLinkedCardIframeLoading(true);

                return financialService.linkFinancialCardUrl().then(response => {
                    this.setLinkFinancialCardUrl(response.data);
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
            } else {
                return Promise.resolve();
            }
        },

        retrieveLinkFinancialCardMaintenanceUrl (): Promise<any> {
            if (!this.linkedCardMaintenanceUrl) {
                this.setLinkedCardIframeLoading(true);

                return financialService.linkFinancialCardMaintenanceUrl().then(response => {
                    this.setLinkedCardMaintenanceUrl(response.data);
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
            } else {
                return Promise.resolve();
            }
        },

        retrieveLinkedCardNeedsMaintenance (): Promise<any> {
            if (this.linkedCardNeedsMaintenanceFetchState === FetchState.Loaded
                || this.linkedCardNeedsMaintenanceFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.setLinkedCardNeedsMaintenanceFetchState(FetchState.Loading);

            return financialService.linkedCardNeedsMaintenance().then(response => {
                this.setLinkedCardNeedsMaintenance(response.data);
                this.setLinkedCardNeedsMaintenanceFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },

        retrieveLinkedCardsList (): Promise<any> {
            expenseService.getLinkedCards().then(response => {
                if (response.data.linkedCards.length === 0) {
                    this.setLinkedCardState(LinkedCardState.Link);
                } else {
                    this.setLinkedCardState(LinkedCardState.List);
                    this.setLinkedCards(response.data.linkedCards);
                }
            }).catch(err => {
                this.setLinkedCardState(LinkedCardState.Error);
                storeHelper.handleError({ error: err, rethrow: true });
            });
            return Promise.resolve();
        },

        connectIFrame (params): Promise<any> {
            const window = params.window;
            const isMaintenance = params.isMaintenance;

            destroyFinicityIFrame(window);
            this.setLinkedCardIframeLoading(true);
            const iframeUrl = isMaintenance ? this.linkedCardMaintenanceUrl : this.linkFinancialCardUrl;

            // https://community.finicity.com/s/article/In-Band-Quickstart-With-Finicity-Connect
            window.finicityConnect.launch(iframeUrl, {
                success (data) {
                    const store = useBankAccountStore();
                    if (isMaintenance) {
                        // setting the status to unitialiazed so we can check the
                        // latest needMaintenance status when a user comes back to the screen
                        store.setLinkedCardNeedsMaintenanceFetchState(FetchState.Uninitialized);
                        store.retrieveLinkedCardNeedsMaintenance();
                    }
                    financialService.refreshCustomerAccount().then(response => {
                        if (response.data) {
                            store.retrieveLinkedCardsList();
                        }
                        store.destroyIFrame(window);
                    });
                },
                error (err) {
                    const store = useBankAccountStore();
                    store.setLinkedCardIframeLoading(false);
                    store.setLinkedCardState(
                        store.linkedCards.length > 0 ? LinkedCardState.List : LinkedCardState.Link);
                    if (err.indexof("iFrame was cancelled") !== -1) {
                        storeHelper.handleError({ error: err, rethrow: true });
                    }
                },
                loaded () {
                    const iframe = document.getElementById("finicityConnectIframe");
                    iframe.setAttribute("style", "width: 100%; height: 735px; overflow: scroll;");
                    document.getElementById("finicityConnectContainer").appendChild(iframe);
                }
            }).catch(err => {
                const store = useBankAccountStore();

                if (err.code !== 100) {
                    storeHelper.handleError({ error: err, rethrow: true });
                }
                store.setLinkedCardIframeLoading(false);
                store.setLinkedCardState(
                    store.linkedCards.length > 0 ? LinkedCardState.List : LinkedCardState.Link);
                store.destroyIFrame(window);
            });

            return Promise.resolve();
        },

        destroyIFrame (window: any): Promise<any> {
            destroyFinicityIFrame(window);
            this.setLinkedCardIframeLoading(false);
            this.setLinkedCardState(this.linkedCards.length > 0 ? LinkedCardState.List : LinkedCardState.Link);
            return Promise.resolve();
        },

        retrieveAchVerifierUrl (window: any): Promise<void> {
            if (!this.achVerifierUrl) {
                this.setAchVerifierUrlState(FetchState.Loading);
                return bankService.getFinicityValidationUrl().then(response => {
                    this.setAchVerifierUrl(response.data);
                    this.setAchVerifierUrlState(FetchState.Loaded);
                    this.connectAchIFrame(window);
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
            }

            return Promise.resolve();
        },

        retrieveAchVerifyAccountUrl (
            params: { window: any, accountId: string }): Promise<void> {
            if (!this.achVerifierUrl) {
                this.setLinkedAccountState(LinkedAccountState.Loading);
                return bankService.getFinicityMicroEntryAccountValidationUrl(params.accountId).then(response => {
                    this.setAchVerifierUrl(response.data);
                    this.connectAchIFrame(params.window);
                    this.setLinkedAccountState(LinkedAccountState.List);
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    this.setLinkedAccountState(LinkedAccountState.List);
                });
            }

            return Promise.resolve();
        },

        connectAchIFrame (window: any): Promise<void> {
            const achVerifierUrl = this.achVerifierUrl;
            this.setAchVerifierUrl(null);
            destroyFinicityIFrame(window);
            this.setAchVerifyIFrameLoaded(true);
            const windowAsAny: any = window;
            windowAsAny.finicityConnect.launch(achVerifierUrl, {
                success (data) {
                    const store = useBankAccountStore();
                    const custodianSettingStore = useCustodianSettingsStore();
                    if (custodianSettingStore.isHsaBankCustodian) {
                        store.setAccountDelayInfoPanelLoaded(true);
                        store.startDelayedIFrameClosure();
                    } else {
                        store.setAccountDelayInfoPanelLoaded(true);
                        store.setAchVerifyIFrameLoaded(false);
                        destroyFinicityIFrame(window);
                    }
                },
                error (err) {
                    const store = useBankAccountStore();
                    store.setAchVerifyIFrameLoaded(false);
                    destroyFinicityIFrame(window);
                    if (err.indexof("iFrame was cancelled") !== -1) {
                        storeHelper.handleError({ error: err, rethrow: true });
                    }
                },
                loaded () {
                    const store = useBankAccountStore();
                    const custodianSettingStore = useCustodianSettingsStore();
                    const iframe = document.getElementById("finicityConnectIframe");
                    iframe.setAttribute("style", "width: 100%; height: 680px; overflow: scroll;");
                    if (custodianSettingStore.isHsaBankCustodian) {
                        document.getElementById("finicityConnectModalContainerAch").appendChild(iframe);
                    } else {
                        document.getElementById("finicityConnectContainerAch").appendChild(iframe);
                    }
                    store.setAchVerifyIFrameLoaded(true);
                }
            }).catch(errObject => {
                this.cleanupRequest();
                const err = (errObject || "").toString();
                if (errObject.code !== 100 && errObject.reason !== "exit") {
                    storeHelper.handleError({ error: err, rethrow: true });
                }
                this.setAchVerifyIFrameLoaded(false);
                // clear out existing accounts and reload, micro deposit verification could
                // have failed and we should make sure we are showing accurate data
                this.setLinkedAccountState(LinkedAccountState.Loading);
                this.setLinkedBankAccountsFetchState(FetchState.Uninitialized);
                this.loadLinkedBankAccounts();
            });
            return Promise.resolve();
        },
        cleanupRequest (): Promise<any> {
            return bankService.externalAccountCleanupRequest()
              .catch(err => {
                storeHelper.handleError({ error: err, rethrow: false, timeout: 3000 });
            });
        },

        closeAccountDelayInfoPanel (): Promise<void> {
            this.setAccountDelayInfoPanelLoaded(false);
            this.clearLinkedBankAccounts();
            this.validateAchBankAccount();

            return Promise.resolve();
        },
        startDelayedIFrameClosure (): Promise<void> {
            return new Promise(resolve => setTimeout(resolve => {
                this.clearLinkedBankAccounts();
                this.validateAchBankAccount();
                this.setAchVerifyIFrameLoaded(false);
                this.setAccountDelayInfoPanelLoaded(false);
                destroyFinicityIFrame(window);
                return Promise.resolve();
            }, 6000));
        },
        deleteMicroEntryAccount (account: MicroEntryVerificationAccount) {
            if (!this.microEntryVerificationAccounts || this.microEntryVerificationAccounts.length === 0) {
                return Promise.resolve();
            }

            this.setLinkedAccountState(LinkedAccountState.Loading);

            const accountId = account.microDepositAccountVerificationId;
            const apiCall = bankService.microEntryAccountDeleteAccount;
            return apiCall(accountId).then(
                response => {
                    this.removeMicroEntryVerificationAccount(accountId);
                    this.setLinkedAccountState(LinkedAccountState.List);
                    return Promise.resolve();
                },
                response => {
                    storeHelper.handleError({ error: "Failed to delete linked account", rethrow: true, timeout: 5000 });
                    this.setLinkedAccountState(LinkedAccountState.List);
                    return Promise.resolve();
                },
            ).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },

        deleteLinkedcard (card: Card): Promise<any> {

            if (!this.linkedCards || this.linkedCards.length === 0) {
                return Promise.resolve();
            }

            this.setLinkedCardState(LinkedCardState.Loading);
            return financialService.deleteLinkedCard(card.id).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            }).finally(() => this.retrieveLinkedCardsList());
        },

        deleteLinkedLogin (loginId: number): Promise<any> {

            if (!this.linkedCards || this.linkedCards.length === 0) {
                return Promise.resolve();
            }

            this.setLinkedCardState(LinkedCardState.Loading);
            return financialService.deleteLinkedLogin(loginId).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            }).finally(() => this.retrieveLinkedCardsList());
        },

        validateAchBankAccount () {
            return bankService.externalAccountAddLinkedAccount()
                .then(model => {
                  if (model.data.accountChoices.length > 0) {
                    this.setLinkedAccountState(LinkedAccountState.Choice);
                    this.setAchBankChoices(model.data.accountChoices);
                  }
                  this.setLinkedBankAccountsFetchState(FetchState.Uninitialized);
                  return this.loadLinkedBankAccounts();
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    this.setLinkedAccounts([]);  // reset this array to the empty state since we failed to get them
                });
        },

        getBankAccountStatus () {
            if (this.accountStatusFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setBankAccountStatusFetchState(FetchState.Loading);

            return bankService
                .getBankAccountStatus()
                .then(response => {
                    this.setBankAccountStatus(response.data.isClosed);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                })
                .finally(() => {
                    this.setBankAccountStatusFetchState(FetchState.Loaded);
                });
        },
        getFullBankAccountNumber (id: number): Promise<string> {
            return bankService.getFullAccountNumber(id)
                .then(response => {
                    return response.data;
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    return "Error";
                });
        },
        getAccountOwnerVerification (id: number): Promise<BankAccountVerificationAccountHolderResponse[]> {
            return bankService.getAccountOwnerVerification(id)
                .then(response => {
                    return response.data;
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    return [];
                });
        },
        getPriorYearContributionCutoffDate () {
            return bankService.getPriorYearContributionCutoffDate()
                .then(result => {
                    this.setPriorYearContributionsCutoffDate(result.data.cutoffDate);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        loadBeneficiaries () {
            if (this.beneficiariesFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.beneficiariesFetchState = FetchState.Loading;

            return bankService.getBeneficiaries()
            .then(response => {
                this.beneficiaries = response.data.beneficiaries.reduce((accumulator, current) => {
                    accumulator[current.id] = current;
                    return accumulator;
                }, {});
            })
            .catch(err => {
                storeHelper.handleError({error: err, rethrow: false });
            })
            .finally(() => {
                this.beneficiariesFetchState = FetchState.Loaded;
            });
        },
        updateBeneficiaries (beneficiary: BeneficiaryUpdateRequest, shares: BeneficiaryShare[]) {
            const beneficiaries = _.cloneDeep(this.beneficiaries);

            beneficiaries[beneficiary.id] = beneficiary;

            if (shares.length > 0) {
                shares.forEach(o => {
                    beneficiaries[o.beneficiaryId].share = o.share;
                });
            }

            this.beneficiaries = beneficiaries;
        },
        addBeneficiary (request: BeneficiaryUpdateRequest) {
            return bankService.addBeneficiary(request)
                .then(response => {
                    const newBeneficiary = response.data.beneficiary;
                    this.updateBeneficiaries(newBeneficiary, request.beneficiaryShares);

                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Your updates will be reviewed and processed.");
                })
                .catch(error => {
                    if (errorIsStatus(error, 409)) {
                        const message = "Unable to add beneficiary. Please <a href='#' onclick='event.preventDefault(); window.location.reload();'>refresh</a> the page and try again.";
                        const notifierStore = useNotifierStore();
                        notifierStore.setDanger(message);
                        return Promise.reject();
                    }

                    return storeHelper.handleError({ error, rethrow: true });
                });
        },
        updateBeneficiary (request: BeneficiaryUpdateRequest) {
            return bankService.updateBeneficiary(request)
                .then(response => {
                    const updatedBeneficiary = response.data.beneficiary;
                    this.updateBeneficiaries(updatedBeneficiary, request.beneficiaryShares);

                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Your updates will be reviewed and processed.");
                })
                .catch(error => {
                    if (errorIsStatus(error, 409)) {
                        const message = "Unable to update beneficiary. Please <a href='#' onclick='event.preventDefault(); window.location.reload();'>refresh</a> the page and try again.";
                        const notifierStore = useNotifierStore();
                        notifierStore.setDanger(message);
                        return Promise.reject();
                    }

                    return storeHelper.handleError({ error, rethrow: true });
                });
        }
    }
});
const destroyFinicityIFrame = (window: any) => {
    if (window && window.finicityConnect !== undefined) {
        window.finicityConnect.destroy();
    }
};

interface ApiCallInput {
    defaultApiCall: (..._) => Promise<AxiosResponse<any>>;
    umbApiCall: (..._) => Promise<AxiosResponse<any>>;
    externalAccountApiCall: (..._) => Promise<AxiosResponse<any>>;
}

function determineApiCallToUse (input: ApiCallInput): (..._) => Promise<AxiosResponse<any>> {
    const custodianSettingStore = useCustodianSettingsStore();
    const userHsaCustodian: BankCustodian = custodianSettingStore.userHsaCustodian;

    if (userHsaCustodian === BankCustodian.Umb) {
        return input.umbApiCall;
    }

    return input.externalAccountApiCall;
}

function fetchLinkedBankAccounts (username?: string, partnerId?: number ) {
    const userStore = useUserStore();
    return userStore.isAdminPortal
        ? bankService.externalAccountGetLinkedAccountAdmin(username, partnerId)
        : bankService.externalAccountGetLinkedAccount(username, partnerId);
}